<template>
  <div class="table-wrapper">
    <employee-list
      @addEmployeeClick="addEmployeeClick()"
      @editEmployeeClick="editEmployeeClick($event)"
      @deleteEmployeeClick="deleteEmployeeClick($event)"
      @deleteEmployeeGroupClick="deleteEmployeeGroupClick($event)"
      @resendEmailClick="resendEmailClick($event)"
      @exportUsersList="exportUsersList($event)"
      @loadData="loadData($event)"
      @import="showImportDialog = true"
      :employees="data"
      :employeesLoading="entityLoading"
      :totalEmployees="totalCount"
    ></employee-list>
    <dialog-wrapper
      ref="employeeDialog"
      :width="dialogWidth"
      @closeDialog="loadData(entitySearchObject)"
    >
      <template v-slot:dialogContent>
        <employee-form
          :dataInfo="employee"
          :mode="mode"
          @closeDialog="closeDialog"
        />
      </template>
    </dialog-wrapper>
    <v-dialog persistent width="680" :value="showImportDialog">
      <employee-import @cancel="showImportDialog = false" />
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import EmployeeList from '@/components/employee/EmployeeList.vue';
import EmployeeForm from '@/components/employee/EmployeeForm.vue';
import EmployeeImport from '@/components/employee/EmployeeImport.vue';
import DialogWrapper from '@/components/elements/DialogWrapper.vue';
import { entityLoader } from '@/mixins/entityLoader.mixins';
import { exportCsv } from 'json2csv-export';
import i18n from '@/plugins/i18n';

export default {
  name: 'list-employee-view',
  components: {
    EmployeeList,
    DialogWrapper,
    EmployeeForm,
    EmployeeImport,
  },
  mixins: [
    entityLoader,
  ],
  data() {
    return {
      employee: null,
      mode: 'add',
      dialogWidth: 600,
      tab: null,
      showImportDialog: false,
    };
  },
  computed: {
    ...mapGetters('corporations', ['getSelectedCorporationId']),
    totalCount() {
      return this.entityTotalCount.totalCount;
    },
  },
  methods: {
    addEmployeeClick() {
      this.mode = 'add';
      this.employee = null;

      this.$refs.employeeDialog.openDialog();
    },
    async editEmployeeClick(employee) {
      this.employee = (
        await this.$store.dispatch('employee/findUsers', {
          ids: [employee.id],
          page: 0,
        })
      ).data[0] || null;

      this.mode = 'edit';
      this.dialogWidth = 600;

      this.$refs.employeeDialog.openDialog();
    },
    closeDialog(submitted) {
      this.$refs.employeeDialog.close();

      if (submitted) this.loadData(this.entitySearchObject);
    },
    async deleteEmployeeClick(employee) {
      this.dialogWidth = 400;
      this.$refs.employeeDialog.openDialog({ type: 'delete-user', id: employee.id });
    },
    async deleteEmployeeGroupClick(employeeGroup) {
      this.dialogWidth = 400;
      this.$refs.employeeDialog.openDialog({ type: 'delete-group', id: employeeGroup.id });
    },
    async resendEmailClick(employee) {
      this.dialogWidth = 400;
      this.$refs.employeeDialog.openDialog({ type: 'resend-email', id: employee.id, corporationId: employee.corporationId });
    },
    async loadData({
      sort, page, maxPerPage, dynamicSearch, tab,
    }) {
      const params = {
        page,
        maxPerPage,
        sort,
        dynamicSearch,
      };
      if (this.getSelectedCorporationId) params.corporationId = this.getSelectedCorporationId;
      if (tab) this.tab = tab;

      await this.getList(`employee/${this.tab}`, params);
    },
    async exportUsersList() {
      const header = {
        id: 'id',
        givenName: i18n.t('usersExport.headers.givenName'),
        familyName: i18n.t('usersExport.headers.familyName'),
        email: i18n.t('usersExport.headers.email'),
        phoneNumber: i18n.t('usersExport.headers.phoneNumber'),
        userType: i18n.t('usersExport.headers.userType'),
        businessAccountId: i18n.t('usersExport.headers.businessAccountId'),
        businessAccountName: i18n.t('usersExport.headers.businessAccountName'),
        createdAt: i18n.t('usersExport.headers.createdAt'),
      };
      const businessPassengers = await this.getBusinessPassengers(0, 50, [this.getSelectedCorporationId], []);
      const businessGuests = await this.getBusinessGuests(0, 50, this.getSelectedCorporationId, []);
      const mappedBusinessPassengers = [];
      businessPassengers.forEach((passenger) => {
        const mappedPassenger = {
          id: passenger.id,
          givenName: passenger.passenger.givenName,
          familyName: passenger.passenger.familyName,
          email: passenger.email || passenger.passenger.email,
          phoneNumber: `${passenger.passenger.phoneNumber}`,
          userType: 'BUSINESS_PASSENGER',
          businessAccountId: passenger.businessAccountId,
          businessAccountName: passenger.businessAccount.name,
          createdAt: passenger.createdAt,
        };
        mappedBusinessPassengers.push(mappedPassenger);
      });
      const mappedBusinessGuests = [];
      businessGuests.forEach((guest) => {
        const mappedGuest = {
          id: guest.id,
          givenName: guest.givenName,
          familyName: guest.familyName,
          email: guest.email,
          phoneNumber: guest.phoneNumber,
          userType: 'BUSINESS_GUEST',
          businessAccountId: guest.businessAccountId,
          businessAccountName: guest.businessAccount.name,
          createdAt: guest.createdAt,
        };
        mappedBusinessGuests.push(mappedGuest);
      });
      const data = [
        ...mappedBusinessPassengers,
        ...mappedBusinessGuests,
      ];
      const { businessAccountName } = data[0];
      const fileName = (businessAccountName) ? `${businessAccountName}-users-export` : 'users-export';
      exportCsv({
        header,
        data,
        filename: fileName.replace(/\s+/g, ''), // file name
      });
    },
    async getBusinessPassengers(page, maxPerPage, businessAccountIds, allPassengers) {
      const passengers = await this.getBusinessPassengersForPage(page, maxPerPage, businessAccountIds, allPassengers);
      const accumulation = [...allPassengers, ...passengers.results];
      if ((passengers.page + 1) * maxPerPage < passengers.total) {
        // eslint-disable-next-line no-underscore-dangle
        return this.getBusinessPassengers(page + 1, maxPerPage, businessAccountIds, accumulation);
      }
      return accumulation;
    },
    async getBusinessPassengersForPage(page, maxPerPage, businessAccountIds) {
      const payload = {
        page,
        maxPerPage,
        businessAccountIds,
      };
      const businessPassengers = await this.$store.dispatch('businessPassenger/getBusinessPassengerList', payload);
      return businessPassengers;
    },
    async getBusinessGuests(page, maxPerPage, businessAccountId, allPassengers) {
      const passengers = await this.getBusinessGuestsForPage(page, maxPerPage, businessAccountId, allPassengers);
      const accumulation = [...allPassengers, ...passengers.results];
      if ((passengers.page + 1) * maxPerPage < passengers.total) {
        return this.getBusinessGuests(page + 1, maxPerPage, businessAccountId, accumulation);
      }
      return accumulation;
    },
    async getBusinessGuestsForPage(page, maxPerPage, businessAccountId) {
      const payload = {
        page,
        maxPerPage,
        businessAccountId,
      };
      const businessGuests = await this.$store.dispatch('businessGuest/getBusinessGuestList', payload);
      return businessGuests;
    },
  },
};
</script>

<style lang="scss" scoped>
.table-wrapper {
  width: 100%;
}
</style>
